import {
  FC,
  useCallback,
  useState,
  useEffect,
  PropsWithChildren,
  createElement,
} from "react";

import { clsxMerge } from "shared/lib/helpers/styles";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { SidebarCloseButton } from "./close-button";

export interface SidebarPropsI extends PropsWithChildren, PropsWithClassNameI {
  /**
   * Content
   */
  title?: string;
  description?: string;

  /**
   * Decorations
   */
  rootClassName?: string;
  headerClassName?: string;
  descriptionClassName?: string;
  closeButtonClassName?: string;
  titleClassName?: string;
  titleWrapperClassName?: string;
  containerClassName?: string;
  backgroundBlurClassName?: string;
  overlayClassName?: string;

  /**
   * Flags
   */
  isFullPage?: boolean;
  isParentSidebar?: boolean;
  isOverlayClickable?: boolean;
  hideTitle?: boolean;
  hideBg?: boolean;
  hideCloseIcon?: boolean;
  hideCloseButton?: boolean;
  disableAnimation?: boolean;

  /**
   * Custom Extensions
   */
  closeButton?: FC<Partial<{ onClick: () => void }>>;

  handleClose?: () => void;
}

interface BackgroundBlurPropsI extends PropsWithClassNameI {
  showBlur?: boolean;
}

export const SIDEBAR_CONTAINER_ID = "sidebar_container_id";
export const SIDEBAR_CLOSE_BTN_ID = "sidebar_close_btn_id";

const BackgroundBlur: FC<BackgroundBlurPropsI> = ({ showBlur, className }) => (
  <div
    className={clsxMerge(
      "absolute left-0 top-0 -z-10 h-full w-full bg-[#F2F2F2]",
      showBlur && "backdrop-blur-lg",
      className
    )}
  />
);

const Sidebar: FC<SidebarPropsI> = ({
  children,

  title,
  description,

  /**
   * Decorations
   */
  className,
  rootClassName,
  headerClassName,
  descriptionClassName,
  closeButtonClassName,
  titleClassName,
  titleWrapperClassName,
  containerClassName,
  overlayClassName,
  backgroundBlurClassName,

  /**
   * Flags
   */
  isFullPage = true,
  isParentSidebar = true,
  isOverlayClickable = true,
  hideTitle,
  hideBg,
  hideCloseIcon,
  hideCloseButton,
  disableAnimation,

  /**
   * Custom Extensions
   */
  closeButton,

  handleClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const prepareForExit = () => {
    handleClose?.();
    // setIsVisible(false);
    // setTimeout(() => handleClose?.(), 500);
  };

  const handleClickOnOverlay = useCallback(() => {
    if (isOverlayClickable) {
      prepareForExit();
    }
  }, [isOverlayClickable]);

  return (
    <div
      className={clsxMerge(
        "left-0 top-0 z-50 h-full w-full",
        isFullPage ? "fixed" : "absolute",
        rootClassName
      )}
    >
      {isParentSidebar && (
        <div
          className={clsxMerge(
            "overlay h-full w-full",
            isVisible ? "bg-black/30" : "bg-black/0 opacity-0",
            !disableAnimation && "transition-opacity duration-500",
            overlayClassName
          )}
          onClick={handleClickOnOverlay}
        />
      )}

      <div
        id={SIDEBAR_CONTAINER_ID}
        className={clsxMerge(
          "absolute right-0 top-0 h-full w-[320px] overflow-hidden",
          "rounded-lg shadow-xl",
          isVisible ? "translate-x-[0]" : "translate-x-[100%]",
          !disableAnimation && "transition-transform duration-500",
          containerClassName
        )}
      >
        <div className="flex h-full flex-col">
          <div
            className={clsxMerge(
              "flex px-6 py-4",
              {
                "justify-end": isParentSidebar && !title && !description,
                "items-center justify-between":
                  !isParentSidebar || title || description,
              },
              headerClassName
            )}
          >
            <div className={clsxMerge("flex flex-col", titleWrapperClassName)}>
              {title && (
                <h3 className={clsxMerge("b-typography-h3", titleClassName)}>
                  {title}
                </h3>
              )}

              {description && (
                <p
                  className={clsxMerge("b-typography-b2", descriptionClassName)}
                >
                  {description}
                </p>
              )}
            </div>

            {!hideCloseButton && (
              <>
                {closeButton ? (
                  createElement(closeButton, { onClick: prepareForExit })
                ) : (
                  <SidebarCloseButton
                    id={SIDEBAR_CLOSE_BTN_ID}
                    className={closeButtonClassName}
                    onClick={prepareForExit}
                    isParentSidebar={isParentSidebar}
                    hideCloseIcon={hideCloseIcon}
                  />
                )}
              </>
            )}
          </div>

          <div
            className={clsxMerge(
              "grow overflow-y-auto overflow-x-hidden px-6 py-4",
              {
                "h-[calc(100vh-64px)]": isFullPage && !hideTitle,
                "h-[100vh]": isFullPage && hideTitle,
              },
              className
            )}
          >
            {children}
          </div>
        </div>

        {!hideBg && (
          <BackgroundBlur
            showBlur={isParentSidebar}
            className={backgroundBlurClassName}
          />
        )}
      </div>
    </div>
  );
};

export default Sidebar;
